/**
 * Onboarding tutorial state (web).
 *
 * The guided step-by-step coach shows for new users and stops auto-appearing
 * permanently once the user has completed their *first* chat save with an ID
 * token (the final step). Everything lives in localStorage so the tour
 * survives refresh and only ever completes once.
 *
 * Follows the same localStorage pattern as `guestPromptStore.ts`.
 */

const ONBOARDING_KEY = 'safepsy_onboarding_v1';

export interface OnboardingState {
  /** True once the user has saved their first session with an ID token. */
  saveCompleted: boolean;
  /** Step the user dismissed last ("Not now"), to resume at the next step. */
  lastDismissedStep?: number;
}

const DEFAULT_STATE: OnboardingState = { saveCompleted: false };

export function readOnboardingState(): OnboardingState {
  try {
    const raw = localStorage.getItem(ONBOARDING_KEY);
    if (!raw) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(raw) as Partial<OnboardingState>;
    return {
      ...DEFAULT_STATE,
      ...parsed,
      saveCompleted: parsed.saveCompleted === true,
    };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

export function writeOnboardingState(state: OnboardingState): void {
  try {
    localStorage.setItem(ONBOARDING_KEY, JSON.stringify(state));
  } catch {
    /* storage unavailable — tour just re-appears next time */
  }
}

/** Permanent gate: after the first save the tour never auto-shows again. */
export function markSaveCompleted(): void {
  const state = readOnboardingState();
  if (state.saveCompleted) return;
  writeOnboardingState({ ...state, saveCompleted: true });
}

export function markStepDismissed(step: number): void {
  const state = readOnboardingState();
  writeOnboardingState({ ...state, lastDismissedStep: step });
}

/** False permanently once the first save is confirmed. */
export function shouldShowOnboarding(): boolean {
  return !readOnboardingState().saveCompleted;
}
